import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConsumerHistoryModel } from '../models/consumer/consumer-history.model';
import { ConsumerModel } from '../models/consumer/consumer.model';
import { HistoryConsumptionModel } from '../models/consumer/history-consumption.model';
import { HistoryCreditStatusModel } from '../models/consumer/history-credit-status.model';
import { HistoryTarrifModel } from '../models/consumer/history-tarrif.model';
import { LatestBalanceModel } from '../models/consumer/latest-balance.model';
import { LatestCreditStatusModel } from '../models/consumer/latest-credit-status.model';
import { LatestMeterReadsModel } from '../models/consumer/latest-meter-reads.model';
import { LatestPaymentModel } from '../models/consumer/latest-payment.model';
import { LatestTarrifModel } from '../models/consumer/latest-tarrif.model';
import { SearchConsumerModel } from '../models/consumer/search-consumer.model';
import { DeviceType } from '../models/consumer/consumer-bulk-interaction.model';

@Injectable()
export class ConsumerService {
    constructor(
        private http: HttpClient,
        @Inject('API_BASE_URL') private baseUrl: string) { }
    private firstQueryString: boolean = false;


    getAll(clientId: number, consumerType: string = ""): Promise<ConsumerModel[]> {

        let url = this.baseUrl + 'client/' + clientId + '/consumers';
        if (consumerType) {
            url = url.concat('?consumerType=' + consumerType);
        }

        let promise = new Promise<ConsumerModel[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as ConsumerModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get all consumers"
                    });
                });
        });

        return promise;

    }

    search(searchTerm: string, page = 1, filter: any[], orderColumn = 'name', orderDirection = 'asc', clientId: number, siteId: number): Observable<Object> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/search';
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            })
        }

        let query = new SearchConsumerModel();
        query.Page = page;
        if (searchTerm && searchTerm.trim().length > 0) {
            query.SearchTerm = searchTerm;
        }

        if (filter.some(x => x == "active")) { query.ShowActive = true; }
        if (filter.some(x => x == "vulnerable")) { query.ShowVulnerable = true; }
        if (filter.some(x => x == "hasissue")) { query.ShowHasIssues = true; }
        if (filter.some(x => x == "billing")) { query.ShowBilling = true; }
        if (filter.some(x => x == "payg")) { query.ShowPayg = true; }
        query.OrderColumn = orderColumn;
        query.OrderDirection = orderDirection;
        query.SiteId = siteId;

        return this.http.post(url, query, httpOptions);

    }

    getHistoryBill(clientId: number, consumerId: string, page: number, pageSize?: number): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/historyBill/' + page;
        this.firstQueryString = true;
        if (pageSize && pageSize > 0) url = url.concat(this.createQueryStringParameter('pageSize', pageSize.toString()));

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get history bill"
                    });
                });
        });

        return promise;
    }

    getConsumerHistory(clientId: number, consumerId: string): Promise<ConsumerHistoryModel[]> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/history';

        let promise = new Promise<ConsumerHistoryModel[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as ConsumerHistoryModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get history bill"
                    });
                });
        });

        return promise;
    }


    getHistoryAccount(clientId: number, page: number, consumerId: string, timePeriod?: number): Promise<any[]> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/historyAccount/' + page;

        if (timePeriod) {
            url = url + '?timePeriod=' + timePeriod;
        }

        let promise = new Promise<any[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as any[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get history account"
                    });
                });
        });

        return promise;
    }

    getHistoryConsumption(clientId: number, consumerId: string, timePeriod: number): Promise<HistoryConsumptionModel[]> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/historyConsumption' + '?timePeriod=' + timePeriod;

        let promise = new Promise<HistoryConsumptionModel[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as HistoryConsumptionModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get history account"
                    });
                });
        });

        return promise;
    }

    getConsumer(clientId: number, consumerId: string): Promise<ConsumerModel> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId;

        let promise = new Promise<ConsumerModel>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    let data: ConsumerModel;
                    data = (res as ConsumerModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get consumer"
                    });
                });
        });

        return promise;
    }

    getConsumerIssues(clientId: number, consumerId: string): Promise<any> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/issues';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get consumer"
                    });
                });
        });

        return promise;
    }

    getLatestTarrif(clientId: number, consumerId: string): Promise<LatestTarrifModel[]> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/latestTarrif';

        let promise = new Promise<LatestTarrifModel[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as LatestTarrifModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get latest tarrif"
                    });
                });
        });

        return promise;
    }

    getLatestPayment(clientId: number, consumerId: string): Promise<LatestPaymentModel> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/latestPayment';

        let promise = new Promise<LatestPaymentModel>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as LatestPaymentModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get latest tarrif"
                    });
                });
        });

        return promise;
    }

    getLatestMeterReads(clientId: number, consumerId: string): Promise<LatestMeterReadsModel[]> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/latestMeterReads';

        let promise = new Promise<LatestMeterReadsModel[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as LatestMeterReadsModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get latest meter reads"
                    });
                });
        });

        return promise;
    }

    getLatestCreditStatus(clientId: number, consumerId: string): Promise<LatestCreditStatusModel> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/latestCreditStatus';

        let promise = new Promise<LatestCreditStatusModel>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as LatestCreditStatusModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get latest tarrif"
                    });
                });
        });

        return promise;
    }

    setCreditStatus(clientId: number, consumerId: string, value: number): Observable<any> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/creditstatus/' + value;
        return this.http.put(url, {});
    }

    setEmergencyCreditLimit(clientId: number, consumerId: string, value: number): Observable<any> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/emergencycreditlimit/' + value;
        return this.http.put(url, {});
    }

    setTariff(clientId: number, consumerId: string, deviceType: DeviceType, value: number): Observable<any> {
        const url = `${this.baseUrl}client/${clientId}/consumers/${consumerId}/tariff/${deviceType}/${value}`;
        return this.http.put(url, {});
    }

    getLatestBalance(clientId: number, consumerId: string): Promise<LatestBalanceModel> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/latestBalance';

        let promise = new Promise<LatestBalanceModel>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as LatestBalanceModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get latest balance"
                    });
                });
        });

        return promise;
    }

    getHistoryCreditStatus(clientId: number, consumerId: string, timePeriod: number): Promise<HistoryCreditStatusModel[]> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/historyCreditStatus' + '?timePeriod=' + timePeriod;

        let promise = new Promise<HistoryCreditStatusModel[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as HistoryCreditStatusModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get history credit status"
                    });
                });
        });

        return promise;

    }

    getHistoryTarrif(clientId: number, consumerId: string): Promise<HistoryTarrifModel[]> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/historyTarrif';

        let promise = new Promise<HistoryTarrifModel[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as HistoryTarrifModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get latest tarrif"
                    });
                });
        });

        return promise;
    }

    gethistoryPaymentsPaged(clientId: number, page: number, consumerId: string, timePeriod: number): Promise<any[]> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/historyPayment/' + page + '?timePeriod=' + timePeriod;

        let promise = new Promise<any[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as any[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get history payments paged"
                    });
                });
        });

        return promise;
    }

    getLatestBill(clientId: number, consumerId: string): Promise<any> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/latestBill';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get history bills paged"
                    });
                });
        });

        return promise;
    }

    gethistoryBillsPaged(clientId: number, page: number, consumerId: string): Promise<any[]> {
        const url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/historyBill/' + page;

        let promise = new Promise<any[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as any[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get history bills paged"
                    });
                });
        });

        return promise;
    }

    getBillDocument(clientId: number, consumerId: string, invoiceNo: string): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/invoices/' + invoiceNo;
      
        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data.base64);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get consumer bills pdf document"
                    });
                });
        });

        return promise;

    }

    private createQueryStringParameter(key: string, value: string) {
        var val = this.firstQueryString ? "?" : "&";
        this.firstQueryString = false;
        return val.concat(key).concat("=".concat(value));
    }
}
